import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/primitives.tsx
function DocRoot({
  children,
  style,
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: className ? `pd-root ${className}` : "pd-root", style, children });
}
function Lead({ children }) {
  return /* @__PURE__ */ jsx("p", { className: "pd-lead", children });
}
function Section({ title, children }) {
  return /* @__PURE__ */ jsxs("section", { className: "pd-section", children: [
    /* @__PURE__ */ jsx("h2", { children: title }),
    children
  ] });
}
function P({ children }) {
  return /* @__PURE__ */ jsx("p", { className: "pd-p", children });
}
function Callout({
  kind = "info",
  title,
  children
}) {
  return /* @__PURE__ */ jsxs("div", { className: `pd-callout pd-callout--${kind}`, children: [
    title && /* @__PURE__ */ jsx("div", { className: "pd-callout__title", children: title }),
    /* @__PURE__ */ jsx("div", { children })
  ] });
}
function Steps({ steps }) {
  return /* @__PURE__ */ jsx("ol", { className: "pd-steps", children: steps.map((s, i) => /* @__PURE__ */ jsxs("li", { className: "pd-step", children: [
    /* @__PURE__ */ jsx("span", { className: "pd-step__num", children: i + 1 }),
    /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsx("span", { className: "pd-step__title", children: s.title }),
      s.body && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("br", {}),
        /* @__PURE__ */ jsx("span", { className: "pd-step__body", children: s.body })
      ] })
    ] })
  ] }, i)) });
}
function Flow({ nodes, vertical = false }) {
  return /* @__PURE__ */ jsx("div", { className: vertical ? "pd-flow pd-flow--vertical" : "pd-flow", children: nodes.map((n, i) => /* @__PURE__ */ jsxs("span", { style: { display: "inline-flex", alignItems: "center", gap: 8 }, children: [
    /* @__PURE__ */ jsxs(
      "span",
      {
        className: n.tone && n.tone !== "default" ? `pd-flow__node pd-flow__node--${n.tone}` : "pd-flow__node",
        children: [
          /* @__PURE__ */ jsx("span", { className: "pd-flow__label", children: n.label }),
          n.sub && /* @__PURE__ */ jsx("span", { className: "pd-flow__sub", children: n.sub })
        ]
      }
    ),
    i < nodes.length - 1 && /* @__PURE__ */ jsx("span", { className: "pd-flow__arrow", "aria-hidden": true, children: vertical ? "\u2193" : "\u2192" })
  ] }, i)) });
}
function Figure({ caption, children }) {
  return /* @__PURE__ */ jsxs("figure", { className: "pd-figure", style: { margin: 0 }, children: [
    /* @__PURE__ */ jsx("div", { className: "pd-figure__frame", children }),
    caption && /* @__PURE__ */ jsx("figcaption", { className: "pd-figure__cap", children: caption })
  ] });
}
function DataTable({ columns, rows }) {
  return /* @__PURE__ */ jsx("div", { className: "pd-table-wrap", children: /* @__PURE__ */ jsxs("table", { className: "pd-table", children: [
    /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsx("tr", { children: columns.map((c) => /* @__PURE__ */ jsx("th", { children: c }, c)) }) }),
    /* @__PURE__ */ jsx("tbody", { children: rows.map((r, i) => /* @__PURE__ */ jsx("tr", { children: r.map((cell, j) => /* @__PURE__ */ jsx("td", { children: cell }, j)) }, i)) })
  ] }) });
}
function Mono({ children }) {
  return /* @__PURE__ */ jsx("code", { className: "pd-mono", children });
}
function CodeBlock({ children }) {
  return /* @__PURE__ */ jsx("pre", { className: "pd-code", children });
}
function WelcomeTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: "Welcome. This portal is where you \u2014 the rights holder for a licensed world \u2014 review and sign off on how your characters, settings, and stories are represented before any of it reaches a child. Nothing goes live in your name until you have seen it and approved it." }),
    /* @__PURE__ */ jsxs(Section, { title: "What this portal is for", children: [
      /* @__PURE__ */ jsx(P, { children: "StoryWorm lets children co-write and illustrate their own stories. When your world is licensed onto the platform, children can choose to set their story inside it \u2014 meeting your characters, in your settings, following your rules. That is a lot of trust to place in an AI system, so this portal exists to keep you in control of it." }),
      /* @__PURE__ */ jsx(P, { children: "Here you can:" }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["You can", "So that"],
          rows: [
            ["See how your world has been captured", "You can confirm it matches your canon"],
            ["Run test stories through it", "You can see what children could actually create"],
            ["Review the rules and guardrails", "You decide what is and isn't allowed"],
            ["Approve reference artwork", "Characters look the way you intend"],
            ["Sign off before anything goes live", "Nothing ships without your explicit approval"]
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "How your world reaches children", children: [
      /* @__PURE__ */ jsx(P, { children: 'Your material travels through a small number of clearly separated stages. You are involved at every stage where a decision matters \u2014 and the final "go live" is always yours.' }),
      /* @__PURE__ */ jsx(Figure, { caption: "Your material is captured, then tested, then certified \u2014 and only then can children build with it.", children: /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "We capture your world", sub: "from your materials" },
            { label: "You review & test", sub: "does it hold up?", tone: "primary" },
            { label: "You certify", sub: "formal sign-off", tone: "success" },
            { label: "Children build", sub: "safely, in canon" }
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "A few ideas worth knowing up front", children: [
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Term", "In plain terms"],
          rows: [
            [
              "Universe",
              "One of your licensed worlds. Each world is kept completely separate from every other \u2014 yours and other studios' alike."
            ],
            [
              "Definition",
              "The structured capture of your world: its characters, settings, look, rules, and what's licensed. It's versioned, so you always know exactly what's live."
            ],
            [
              "Validation",
              "Testing that generated stories and pictures stay true to your world before they're allowed."
            ],
            [
              "Certification",
              "Your formal sign-off that a version of your world is ready \u2014 plus a tamper-evident record proving it."
            ]
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "You are never automated out of the loop", children: "The system can suggest, extract, and flag \u2014 but the decisions that carry your brand (what's canon, what's allowed, what goes live) always require a person on your side to confirm." })
    ] })
  ] });
}
function YourUniverseTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: `Before children can build in your world, we capture it into a structured, versioned "definition" \u2014 the single, reviewable description of what your world is and what may be done with it. This page explains what we ask for, how it's turned into that definition, and the guarantees that come with it.` }),
    /* @__PURE__ */ jsxs(Section, { title: "What you provide", children: [
      /* @__PURE__ */ jsx(P, { children: "There's no single required format. You give us the materials you already have, and we adapt to them \u2014 whether that's a polished story bible or a folder of character art and a licence agreement." }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Material", "Examples", "What it becomes"],
          rows: [
            ["Written canon", "Story bibles, character briefs, world guides (PDF, docs, or text)", "Characters, settings, and the rules of your world"],
            ["Artwork", "Character sheets, style guides, reference renders", "The visual truth used to keep pictures on-model"],
            ["Rules & do-nots", "Tone guides, brand safety lists, 'never do this' notes", "Guardrails enforced on every story"],
            ["Rights & licence terms", "What's licensed, where, and how", "The permissions that gate what can be made"]
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "info", title: "Everything is traceable to its source", children: "Every fact we capture records where it came from \u2014 which document, which page. When you're reviewing, you can always see the source behind a captured rule or character detail, so nothing is taken on faith." })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "How capture works", children: [
      /* @__PURE__ */ jsxs(P, { children: [
        'We call the capture process "ingestion". It reads your materials and ',
        /* @__PURE__ */ jsx("em", { children: "proposes" }),
        " ",
        "additions to your world's definition \u2014 it never silently rewrites it. Proposals are grouped so you can review them efficiently."
      ] }),
      /* @__PURE__ */ jsx(Figure, { caption: "Your materials produce proposals; your team reviews them; approved proposals become part of your world.", children: /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "Your materials" },
            { label: "Proposed additions", sub: "with sources", tone: "primary" },
            { label: "Your review", sub: "approve / reject" },
            { label: "Your world's definition", tone: "success" }
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(
        Steps,
        {
          steps: [
            {
              title: "We capture proposals from your materials",
              body: "New characters, settings, rules, artwork, and rights \u2014 each with a confidence indication and its source."
            },
            {
              title: "You review anything that matters",
              body: "You can auto-accept clearly-correct new details if you wish, but anything that changes or contradicts something already agreed is always brought to you."
            },
            {
              title: "Approved proposals join the working definition",
              body: "This builds up a draft version of your world that you can test before it's ever certified."
            }
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "Capture is incremental", children: "You don't have to hand everything over at once. You can add materials over time; each new batch is reconciled against what's already there, and any change to existing canon is flagged for your confirmation." })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "What a 'definition' contains", children: [
      /* @__PURE__ */ jsx(P, { children: "Your world's definition brings four things together in one reviewable place:" }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Part", "What it captures"],
          rows: [
            ["Characters, settings & props", "Who and what is in your world \u2014 including variants like different outfits, ages, or forms \u2014 and the reference art for each."],
            ["Rules", "How your characters behave, how your world works, how stories should be shaped, and how everything should look."],
            ["Style", "The overall visual identity of your world, beyond any single character."],
            ["Rights", "Exactly what is licensed \u2014 per character, per use, per channel, per territory."]
          ]
        }
      ),
      /* @__PURE__ */ jsxs(Callout, { kind: "info", title: "Versioned and isolated \u2014 two promises", children: [
        "Your world's definition is ",
        /* @__PURE__ */ jsx("strong", { children: "versioned" }),
        ": once you certify a version it is frozen, so what children build with never shifts under you. And it is",
        " ",
        /* @__PURE__ */ jsx("strong", { children: "isolated" }),
        ": your world is never merged or blended with any other \u2014 not another studio's, and not another of your own worlds."
      ] })
    ] })
  ] });
}
function ValidationTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: "Validation is how you satisfy yourself \u2014 before certifying \u2014 that stories and pictures made in your world actually stay true to it. You run stories through the same guardrails that will protect your world in production, and you see exactly where anything strays." }),
    /* @__PURE__ */ jsx(Section, { title: "Why you'd do this", children: /* @__PURE__ */ jsx(P, { children: "A definition can look perfect on paper and still behave in ways you didn't intend once a child starts creating. Validation lets you pressure-test your world with real and deliberately awkward stories, and tune the rules until you're comfortable \u2014 all before it goes live." }) }),
    /* @__PURE__ */ jsxs(Section, { title: "It's validated twice \u2014 and the second time is yours", children: [
      /* @__PURE__ */ jsxs(P, { children: [
        "Nothing reaches you unchecked. StoryWorm runs a full round of validation on your world",
        " ",
        /* @__PURE__ */ jsx("em", { children: "first" }),
        " \u2014 so that what you're handed is already fit to review. Only then is a version released to you here in the portal for your ",
        /* @__PURE__ */ jsx("strong", { children: "own" }),
        " validation and approval."
      ] }),
      /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "StoryWorm validates", sub: "before you see it", tone: "primary" },
            { label: "You validate & approve", sub: "here in the portal", tone: "primary" },
            { label: "Certified", sub: "ready to go live", tone: "success" }
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "Your approval is the gate", children: "A version cannot be certified \u2014 and therefore cannot go live \u2014 until you have validated and approved it yourself. The first round gets it ready; the second round is the one that counts." })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "Two kinds of checking", children: [
      /* @__PURE__ */ jsxs(P, { children: [
        "Every story is checked two ways: the ",
        /* @__PURE__ */ jsx("strong", { children: "words" }),
        " and the",
        " ",
        /* @__PURE__ */ jsx("strong", { children: "pictures" }),
        ". Both produce the same simple result \u2014 a pass, or a list of issues, each marked by how serious it is."
      ] }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Check", "Looks for", "Examples of what it catches"],
          rows: [
            [
              "Story (words)",
              "Whether the text respects your characters, world, and tone",
              "A character acting out of character; a banned theme; missing signature language"
            ],
            [
              "Illustration (pictures)",
              "Whether images are on-model, on-style, and brand-safe",
              "A character who doesn't look like themselves; off-brand styling; unsafe imagery"
            ]
          ]
        }
      ),
      /* @__PURE__ */ jsxs(Callout, { kind: "info", title: "How results are graded", children: [
        "Each issue is labelled ",
        /* @__PURE__ */ jsx("strong", { children: "info" }),
        ", ",
        /* @__PURE__ */ jsx("strong", { children: "warning" }),
        ",",
        " ",
        /* @__PURE__ */ jsx("strong", { children: "error" }),
        ", or ",
        /* @__PURE__ */ jsx("strong", { children: "critical" }),
        ", and \u2014 where possible \u2014 points to the exact rule it breaks and suggests a fix. You decide which severities are acceptable for your brand."
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "How the words are checked", children: [
      /* @__PURE__ */ jsx(P, { children: "Text checking happens in two passes. A fast, exact pass catches clear-cut breaches \u2014 banned words, forbidden actions, missing required language. A second, smarter pass reads the story in context against your canon to catch the subtler things a word-list can't, like a character behaving in a way that's technically allowed but wrong for who they are." }),
      /* @__PURE__ */ jsx(Figure, { caption: "A quick exact check, then a context-aware check. An issue can be raised by either.", children: /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "Story text" },
            { label: "Exact check", sub: "clear breaches", tone: "warning" },
            { label: "Context check", sub: "subtler issues", tone: "primary" },
            { label: "Result", sub: "pass or issues", tone: "success" }
          ]
        }
      ) })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "How the pictures are checked", children: [
      /* @__PURE__ */ jsx(P, { children: "Illustrations are conditioned on your approved reference art, then verified in stages:" }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Stage", "Question it answers"],
          rows: [
            ["Identity", "Is this actually your character, and the right version of them?"],
            ["Style", "Does it match your world's visual style?"],
            ["Safety", "Is anything present that breaches your brand-safety rules?"]
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "Running a validation", children: [
      /* @__PURE__ */ jsx(
        Steps,
        {
          steps: [
            {
              title: "Pick or write test stories",
              body: "Use everyday stories a child might write, plus a few deliberately provocative ones to probe the edges."
            },
            {
              title: "Run them through your world",
              body: "You'll get a report for each: a pass, or a list of issues with severities and, where possible, suggested fixes."
            },
            {
              title: "Tune and re-run",
              body: "Adjust the rules or references and run again until the results meet your standard."
            }
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "This is the same engine that protects production", children: "You aren't testing a demo. The checks you run here are the exact guardrails that will run on every child's story once your world is live \u2014 so what you approve is what you get." })
    ] })
  ] });
}
function ReferencesTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: "Reference art is the visual truth of your world \u2014 the pictures the system uses to keep every illustration on-model. You control which images count as canon, and your approval is what turns any image into an allowed reference." }),
    /* @__PURE__ */ jsx(Section, { title: "Why references matter so much", children: /* @__PURE__ */ jsx(P, { children: "When a child's story features one of your characters, the illustrator conditions the image on your reference art so the character looks like themselves \u2014 the right face, outfit, and style. Better and more complete references mean more consistent, more on-brand pictures. Weak references mean drift, which is exactly what you don't want carrying your name." }) }),
    /* @__PURE__ */ jsxs(Section, { title: "Two kinds of reference", children: [
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Kind", "Where it comes from", "Status"],
          rows: [
            [
              "Canonical",
              "Art you provide \u2014 official character sheets, style guides, key renders",
              "Trusted by default; this is your source of truth"
            ],
            [
              "Approved-generated",
              "An image the system created that you then approve",
              "Becomes a first-class reference once you sign off"
            ]
          ]
        }
      ),
      /* @__PURE__ */ jsxs(Callout, { kind: "info", title: "Approved-generated images become canon too", children: [
        "When the system produces an especially good, on-model image of a character, you can promote it to a reference. From then on it helps condition future pictures \u2014 but only because",
        " ",
        /* @__PURE__ */ jsx("em", { children: "you" }),
        " approved it. Nothing generated becomes reference material on its own."
      ] })
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "Variants: same character, different look", children: [
      /* @__PURE__ */ jsx(P, { children: "Characters aren't always one fixed image. A character might have different outfits, ages, or forms \u2014 and each can carry its own reference art, so the right look is used at the right moment in a story. If your hero puts on a spacesuit halfway through, the illustrator knows to use the spacesuit references." }),
      /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "Character" },
            { label: "Everyday outfit", sub: "its own references" },
            { label: "Spacesuit", sub: "its own references" }
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx(Section, { title: "Your approval, recorded", children: /* @__PURE__ */ jsx(P, { children: "Every reference carries a record of your sign-off \u2014 who approved it and when. That record isn't just bookkeeping: it feeds the provenance trail attached to finished artwork, so there's always a clear line from a published picture back to the references you approved." }) })
  ] });
}
function CertificationTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: "Certification is your formal sign-off that a version of your world is ready for children to build with \u2014 and the mechanism that later proves any picture made from it was created under your licence and passed your checks. It's how your approval becomes verifiable, not just a promise." }),
    /* @__PURE__ */ jsxs(Section, { title: "Certifying vs going live \u2014 two separate steps", children: [
      /* @__PURE__ */ jsx(P, { children: "These are deliberately kept apart, so a new version can never surprise you by going live on its own." }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Step", "What it means", "Reversible?"],
          rows: [
            ["Certify", "You freeze a version and confirm it's ready", "It's frozen, but you can certify newer versions later"],
            ["Go live", "You choose which certified version children actually use", "Yes \u2014 you can change which version is live at any time"]
          ]
        }
      ),
      /* @__PURE__ */ jsx(Figure, { caption: "A newly certified version does not go live automatically \u2014 you point production at it when you're ready.", children: /* @__PURE__ */ jsx(
        Flow,
        {
          nodes: [
            { label: "In testing" },
            { label: "Certified", sub: "frozen + signed off", tone: "primary" },
            { label: "Live", sub: "you choose when", tone: "success" }
          ]
        }
      ) }),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "Why this protects you", children: "Because certifying and going live are separate, you can prepare and certify an updated version of your world without disturbing what children are building with today. You flip the switch only when you're ready." })
    ] }),
    /* @__PURE__ */ jsx(Section, { title: "What certification records", children: /* @__PURE__ */ jsx(P, { children: "When you certify, the platform writes a tamper-evident record that pins the exact version you approved, together with the evidence behind it \u2014 the reference art used and the validation results that passed. These records are chained together, so the history of your world's approvals can't be quietly altered after the fact." }) }),
    /* @__PURE__ */ jsxs(Section, { title: "Provenance on every picture", children: [
      /* @__PURE__ */ jsx(P, { children: "Every illustration produced from your world carries an invisible, tamper-resistant record of where it came from: which world, which certified version, and that it passed your checks. This uses industry-standard content credentials plus an invisible watermark, so the provenance survives even if the file is copied or the visible credentials are stripped." }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["Carried with each image", "What it proves"],
          rows: [
            ["Content credentials", "Which world and version made it, and that it passed conformance"],
            ["Invisible watermark", "That it's AI-generated, in a way that survives copying and editing"],
            ["A machine-readable AI marking", "Transparency that it was AI-generated"]
          ]
        }
      ),
      /* @__PURE__ */ jsx(Callout, { kind: "info", title: "Built for the EU AI Act (Article 50)", children: "From 2 August 2026, AI-generated media must be transparently marked. The content credentials and watermark carried with every picture are designed to meet that obligation \u2014 so using StoryWorm keeps your licensed content on the right side of the rules." })
    ] })
  ] });
}
function RightsTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: 'Modern licensing is rarely "all or nothing". You might licence one character but not another, for apps but not print, in some regions but not others. The platform models rights the same granular way your agreements do \u2014 so what can be created always matches what you actually licensed.' }),
    /* @__PURE__ */ jsxs(Section, { title: "Rights are per-character, not one blanket", children: [
      /* @__PURE__ */ jsx(P, { children: "Instead of a single switch for your whole world, each character (or setting, voice, or likeness) carries its own grant. That means you can bring one hero online while holding others back, or licence a supporting cast for one channel only \u2014 all within the same world." }),
      /* @__PURE__ */ jsx(
        DataTable,
        {
          columns: ["A grant specifies", "In practice"],
          rows: [
            ["Permitted uses", "What may be done with the character"],
            ["Channels", "Where it may appear \u2014 e.g. in-app, print, streaming"],
            ["Territories", "Which regions it's licensed for"],
            ["Exclusions", "Explicit carve-outs that override the above"],
            ["Output ownership", "Who owns AI-generated results: the platform, you, or shared"],
            ["Validity window", "When the grant starts and ends, if time-limited"]
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Section, { title: "Rights are enforced, not just recorded", children: [
      /* @__PURE__ */ jsx(P, { children: "These grants aren't a filing cabinet \u2014 they gate what the platform will make. Production of content for a character is blocked unless there's a live, certified version of your world whose rights permit it. If a grant lapses or is excluded, the platform simply won't generate against it." }),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "Ownership of what's created", children: "Because AI outputs derived from your IP can be a sensitive point, each grant states plainly who owns them \u2014 you, the platform, or both \u2014 so there's no ambiguity later." })
    ] })
  ] });
}
function TrustTopic() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Lead, { children: "Licensing a beloved world to an AI platform is a serious act of trust. This page answers the questions rights holders ask us most \u2014 about isolation, who can see what, safety, and the record you can rely on." }),
    /* @__PURE__ */ jsx(Section, { title: "Is my world kept separate from others?", children: /* @__PURE__ */ jsx(P, { children: "Yes \u2014 completely. Each world is its own isolated boundary. Your content is never blended with another studio's, and never with another of your own worlds. There is no shared pool of characters or art across licensors." }) }),
    /* @__PURE__ */ jsx(Section, { title: "Who can see my world?", children: /* @__PURE__ */ jsx(
      DataTable,
      {
        columns: ["Who", "Can see"],
        rows: [
          ["Your team", "Your organization's worlds only"],
          ["StoryWorm staff overseeing the platform", "Worlds they're supporting, for validation and certification"],
          ["Other licensors", "Nothing of yours \u2014 ever"],
          ["Children using the app", "Only your certified, live content \u2014 and only as guardrailed stories"]
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(Section, { title: "How is child safety handled?", children: /* @__PURE__ */ jsx(P, { children: "StoryWorm is a children's product, so brand safety and child safety reinforce each other. Your brand-safety rules are enforced on both the words and the pictures of every story, and nothing your world produces goes live until you've certified it. The same guardrails you approve here run on every child's creation." }) }),
    /* @__PURE__ */ jsxs(Section, { title: "What proof do I have of what was approved?", children: [
      /* @__PURE__ */ jsx(P, { children: "Every certification is a tamper-evident, chained record pinning the exact version you approved and the evidence behind it. Every generated picture carries provenance back to that certified version. If you ever need to demonstrate what was licensed, approved, and produced, the trail is there." }),
      /* @__PURE__ */ jsx(Callout, { kind: "tip", title: "Nothing ships without you", children: "The single most important guarantee: no version of your world becomes available to children until a person on your side has certified it, and you can change or withdraw what's live at any time." })
    ] })
  ] });
}

// src/registry.tsx
var PARTNER_DOC_TOPICS = [
  {
    id: "welcome",
    title: "Welcome",
    summary: "What this portal is for and how your world reaches children.",
    Component: WelcomeTopic
  },
  {
    id: "your-universe",
    title: "Your universe",
    summary: "What we capture from your materials, and the guarantees that come with it.",
    Component: YourUniverseTopic
  },
  {
    id: "validation",
    title: "Understanding validation",
    summary: "Testing that stories and pictures stay true to your world.",
    Component: ValidationTopic
  },
  {
    id: "references",
    title: "Reference art & approvals",
    summary: "The visual truth of your world, and how your approval makes it canon.",
    Component: ReferencesTopic
  },
  {
    id: "certification",
    title: "Certification & provenance",
    summary: "Your formal sign-off, and the proof carried by everything produced.",
    Component: CertificationTopic
  },
  {
    id: "rights",
    title: "Rights & licensing",
    summary: "How granular grants control what can be created.",
    Component: RightsTopic
  },
  {
    id: "trust",
    title: "Data, isolation & trust",
    summary: "Isolation, visibility, child safety, and the record you can rely on.",
    Component: TrustTopic
  }
];
function getPartnerDocTopic(id) {
  return PARTNER_DOC_TOPICS.find((t) => t.id === id);
}

export { Callout, CodeBlock, DataTable, DocRoot, Figure, Flow, Lead, Mono, P, PARTNER_DOC_TOPICS, Section, Steps, getPartnerDocTopic };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map